import {
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseCircleFilled,
  DollarOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { Stack, Typography } from '@sureco/design-system'
import { Skeleton } from '@surecompanies/core_components'
import { Progress } from 'antd'
import React from 'react'
import { DemoContributionStatusResponse } from '../types'

export const CONTRIBUTION_RUN_NOT_STARTED_MESSAGE =
  'Contribution run could not be started'

const RUN_FAILED_FALLBACK_MESSAGE = 'The contribution run did not finish.'

interface DemoSetupProgressProps {
  /** Null when the company never had a demo contribution run. */
  contributionStatus: DemoContributionStatusResponse | null
  /**
   * True when populate succeeded but returned no contributionRunJobId. Shown
   * as Failed until a real run exists (or the page is reloaded).
   */
  runNotStarted?: boolean
  isLoading?: boolean
}

type DisplayState = 'not-set' | 'in-progress' | 'completed' | 'failed'

interface StateStyle {
  title: string
  titleColor: string
  accent: string
  background: string
  icon: React.ReactNode
}

const STATE_STYLES: Record<DisplayState, StateStyle> = {
  'not-set': {
    title: 'Not set',
    titleColor: '#1677ff',
    accent: '#1677ff',
    background: '#e6f4ff',
    icon: <DollarOutlined />,
  },
  'in-progress': {
    title: 'In progress',
    titleColor: '#fa8c16',
    accent: '#fa8c16',
    background: '#fff7e6',
    icon: <LoadingOutlined />,
  },
  completed: {
    title: 'Completed',
    titleColor: '#262626',
    accent: '#52c41a',
    background: '#f6ffed',
    icon: <CheckCircleFilled />,
  },
  failed: {
    title: 'Failed',
    titleColor: '#ff4d4f',
    accent: '#ff4d4f',
    background: '#fff1f0',
    icon: <CloseCircleFilled />,
  },
}

function resolveDisplayState(
  contributionStatus: DemoContributionStatusResponse | null,
  runNotStarted: boolean
): DisplayState {
  if (!contributionStatus) return runNotStarted ? 'failed' : 'not-set'
  return contributionStatus.status
}

function describeState(
  state: DisplayState,
  contributionStatus: DemoContributionStatusResponse | null
): string {
  switch (state) {
    case 'in-progress': {
      const stepLabel = contributionStatus?.stepLabel
      const percent = contributionStatus?.percent
      return stepLabel && typeof percent === 'number'
        ? `${stepLabel} · ${Math.round(percent)}%`
        : 'Applying the employer contribution strategy…'
    }
    case 'completed':
      return 'Employer contribution strategy applied to all plans.'
    case 'failed':
      return contributionStatus
        ? (contributionStatus.errorMessage ?? RUN_FAILED_FALLBACK_MESSAGE)
        : CONTRIBUTION_RUN_NOT_STARTED_MESSAGE
    default:
      return 'Employer contribution strategy has not been set up yet.'
  }
}

export default function DemoSetupProgress({
  contributionStatus,
  runNotStarted = false,
  isLoading = false,
}: DemoSetupProgressProps) {
  const state = resolveDisplayState(contributionStatus, runNotStarted)
  const style = STATE_STYLES[state]
  const percent =
    state === 'in-progress' ? contributionStatus?.percent : undefined

  return (
    <div
      style={{
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        padding: 16,
        background: '#fff',
      }}
    >
      <Typography
        size={13}
        style={{ fontWeight: 600, color: '#003146', marginBottom: 12 }}
      >
        Contributions
      </Typography>

      {isLoading ? (
        <Skeleton
          active
          avatar={{ size: 32 }}
          paragraph={{ rows: 1 }}
          title={false}
        />
      ) : (
        <>
          <Stack direction="row" space={10} align="center">
            <div
              data-testid="contribution-status-icon"
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: style.background,
                border: `2px solid ${style.accent}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 15,
                color: style.accent,
                flexShrink: 0,
              }}
            >
              {style.icon}
            </div>

            <Stack space={2} style={{ flex: 1, minWidth: 0 }}>
              <Typography
                size={13}
                style={{ fontWeight: 600, color: style.titleColor }}
              >
                {style.title}
              </Typography>
              <Typography
                size={12}
                color="#8c8c8c"
                style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {describeState(state, contributionStatus)}
              </Typography>
            </Stack>
          </Stack>

          {typeof percent === 'number' && (
            <Progress
              percent={Math.round(percent)}
              showInfo={false}
              size="small"
              strokeColor={style.accent}
              style={{ marginTop: 8, marginBottom: 0 }}
            />
          )}

          {state === 'completed' && (
            <div
              style={{
                marginTop: 12,
                padding: '6px 10px',
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: 6,
              }}
            >
              <Stack direction="row" space={6} align="center">
                <ClockCircleOutlined
                  style={{ color: '#52c41a', fontSize: 12 }}
                />
                <Typography size={12} style={{ color: '#389e0d' }}>
                  Contributions are ready for the demo.
                </Typography>
              </Stack>
            </div>
          )}
        </>
      )}
    </div>
  )
}
